// ------------------------------------------------------------------------------
// 初期設定 - 状態更新
// ------------------------------------------------------------------------------
import configureStore from '../reducks/store';
import {authOperations} from '../reducks/auth';
import {commonOperations} from '../reducks/common';
import {runStatOperations} from '../reducks/runstat';
import {snackOperations} from '../reducks/snackbar';
import snackbar from './snackbar';
import errors from './errors';

// Reduxストア
const reduxStore = configureStore();
const {dispatch, getState} = reduxStore;

/**
 * 認証ハッシュ更新
 *
 * - HTTP応答ヘッダーの認証情報 (ユーザー属性のハッシュ値) を取得
 * - Reduxストアに認証情報を保存 (次回リクエストのヘッダーに設定)
 * - バックエンドはこの値を使用してユーザーの属性変更を検知する
 *
 * @param {HeaderValue} authHeader 認証情報ヘッダー
 */
export async function updateAuthorizedHash(authHeader) {
  if (!authHeader) {
    return Promise.resolve();
  }

  // Reduxストアに保存
  const authorizedHash = authHeader.value;
  return dispatch(authOperations.doSetAuthorizedHash(authorizedHash));
}

/**
 * 業務運用状態更新
 *
 * - HTTP応答ヘッダーの運用状態をストアに保存
 *
 * @param {HeaderValue} statHeader 運用状態ヘッダー
 */
export async function updateRunningStatus(statHeader) {
  if (!statHeader) {
    return Promise.resolve();
  }

  // 運用状態を保存
  const currentStatus = statHeader.value;
  const comingStatus = statHeader.option('soon');
  const comingSoon = (comingStatus !== currentStatus) ? comingStatus : null;
  const comingLast = getState().runstat?.coming;
  const newStatus = {
    current: currentStatus,
    coming: comingSoon,
  };
  const promiseUpdate = dispatch(runStatOperations.doUpdateStatus(newStatus));

  // 運用状態予定の変更判定
  if (comingSoon === comingLast) {
    return promiseUpdate;
  }
  console.log(`運用状態予定変更: ${comingLast} -> ${comingSoon}`);

  // 運用状態予告メッセージを更新
  const messageLast = snackbar.comingMessage(comingLast);
  const messageSoon = snackbar.comingMessage(comingSoon);
  return Promise.all([
    promiseUpdate,
    messageLast && dispatch(snackOperations.deleteMessage(messageLast)),
    messageSoon && dispatch(snackOperations.appendMessage(messageSoon)),
  ]);
}

/**
 * 業務運用状態解析
 *
 * - HTTP応答ヘッダーの運用状態をストアに保存
 * - 運用状態が変わった場合はメッセージを表示
 *
 * @param {HeaderValue} statHeader 運用状態ヘッダー
 */
export async function parseRunningStatus(statHeader) {
  if (!statHeader) {
    return Promise.resolve();
  }
  const currentStatus = statHeader.value;             // 現在の状態
  const lastStatus = getState().runstat?.current;     // 直前の状態

  // 業務運用状態更新
  updateRunningStatus(statHeader);

  // 運用状態が変わっていない場合/初回実行時は処理終了
  if (currentStatus === lastStatus || !lastStatus) {
    return Promise.resolve();
  }
  console.log(`業務運用状態変更: ${lastStatus} -> ${currentStatus}`);

  // 運用状態変更メッセージ取得
  const guide = await errors.backendMessage(503, currentStatus);
  if (!guide) {
    return Promise.resolve();
  }

  // 運用状態予告メッセージ消去
  snackbar.clearComing(dispatch);

  // メッセージダイアログ表示
  const dialog = {
    highPriority: false,   // 正常終了時のみ表示
    message: guide.message,
    action: guide.action,
  };
  return dispatch(commonOperations.doShowMessage(dialog));
}

// ------------------------------------------------------------------------------
// エクスポート
// ------------------------------------------------------------------------------
export default {
  updateAuthorizedHash,
  updateRunningStatus,
  parseRunningStatus,
};
